import React from 'react';
import { useAuth } from '../context/AuthContext';
import { getCountryNames } from '../data/countries';

export const RegionTestComponent: React.FC = () => {
  const { 
    partners, 
    advertisements, 
    partnerCampaigns, 
    marketingCampaigns 
  } = useAuth();

  const testRegionConsistency = () => {
    console.log('=== REGION CONSISTENCY TEST ===');
    
    // Test 1: Check if all advertisements have regions
    const adsWithoutRegion = advertisements.filter(ad => !ad.region);
    console.log('Advertisements without region:', adsWithoutRegion.length);
    
    // Test 2: Check if all partner campaigns have regions
    const campaignsWithoutRegion = partnerCampaigns.filter(pc => !pc.region);
    console.log('Partner campaigns without region:', campaignsWithoutRegion.length);
    
    // Test 3: Check region consistency between campaigns and their advertisements
    const inconsistentCampaigns = partnerCampaigns.filter(pc => {
      if (!pc.region) return false;
      
      const campaignAds = advertisements.filter(ad => 
        pc.advertisement_ids.includes(ad.id)
      );
      
      return campaignAds.some(ad => ad.region !== pc.region);
    });
    
    console.log('Campaigns with region inconsistencies:', inconsistentCampaigns.length);

    // Test 4: Check one active campaign per region constraint
    const partnerActiveRegionViolations: Array<{
      partnerId: string;
      partnerName: string;
      region: string;
      count: number;
    }> = [];
    
    partners.forEach(partner => {
      const partnerActiveCampaigns = partnerCampaigns.filter(pc => 
        pc.partner_id === partner.id && pc.status === 'active'
      );
      
      const regionCounts: Record<string, number> = {};
      partnerActiveCampaigns.forEach(pc => {
        if (pc.region) {
          regionCounts[pc.region] = (regionCounts[pc.region] || 0) + 1;
        }
      });
      
      Object.entries(regionCounts).forEach(([region, count]) => {
        if (count > 1) {
          partnerActiveRegionViolations.push({
            partnerId: partner.id,
            partnerName: partner.name,
            region,
            count: count as number
          });
        }
      });
    });
    
    console.log('Partners with multiple active campaigns in same region:', partnerActiveRegionViolations);
    
    // Test 4: Check partner region assignments
    partners.forEach(partner => {
      console.log(`Partner ${partner.name}:`, {
        regions: partner.region || [],
        regionCount: partner.region?.length || 0
      });
    });
    
    // Test 5: Show region distribution
    const regionDistribution: {
      advertisements: Record<string, number>;
      campaigns: Record<string, number>;
    } = {
      advertisements: {},
      campaigns: {}
    };
    
    advertisements.forEach(ad => {
      if (ad.region) {
        regionDistribution.advertisements[ad.region] = 
          (regionDistribution.advertisements[ad.region] || 0) + 1;
      }
    });
    
    partnerCampaigns.forEach(pc => {
      if (pc.region) {
        regionDistribution.campaigns[pc.region] = 
          (regionDistribution.campaigns[pc.region] || 0) + 1;
      }
    });
    
    console.log('Region distribution:', regionDistribution);
    
    return {
      adsWithoutRegion: adsWithoutRegion.length,
      campaignsWithoutRegion: campaignsWithoutRegion.length,
      inconsistentCampaigns: inconsistentCampaigns.length,
      activeRegionViolations: partnerActiveRegionViolations.length,
      regionDistribution
    };
  };

  const testResults = testRegionConsistency();

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">Region System Test Results</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900">Total Partners</h4>
          <p className="text-2xl font-bold text-blue-600">{partners.length}</p>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="font-medium text-green-900">Total Advertisements</h4>
          <p className="text-2xl font-bold text-green-600">{advertisements.length}</p>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <h4 className="font-medium text-purple-900">Partner Campaigns</h4>
          <p className="text-2xl font-bold text-purple-600">{partnerCampaigns.length}</p>
        </div>
        
        <div className="bg-orange-50 p-4 rounded-lg">
          <h4 className="font-medium text-orange-900">Marketing Campaigns</h4>
          <p className="text-2xl font-bold text-orange-600">{marketingCampaigns.length}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${testResults.adsWithoutRegion === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
          <h4 className="font-medium">✓ Advertisements Region Check</h4>
          <p>Advertisements without region: <strong>{testResults.adsWithoutRegion}</strong></p>
        </div>
        
        <div className={`p-4 rounded-lg ${testResults.campaignsWithoutRegion === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
          <h4 className="font-medium">✓ Campaigns Region Check</h4>
          <p>Campaigns without region: <strong>{testResults.campaignsWithoutRegion}</strong></p>
        </div>
        
        <div className={`p-4 rounded-lg ${testResults.inconsistentCampaigns === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
          <h4 className="font-medium">✓ Region Consistency Check</h4>
          <p>Campaigns with region inconsistencies: <strong>{testResults.inconsistentCampaigns}</strong></p>
        </div>
        
        <div className={`p-4 rounded-lg ${testResults.activeRegionViolations === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
          <h4 className="font-medium">✓ One Active Campaign Per Region Rule</h4>
          <p>Partners violating the rule: <strong>{testResults.activeRegionViolations}</strong></p>
          <p className="text-xs text-gray-600 mt-1">Each partner should have max 1 active campaign per region</p>
        </div>
        
        <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
          <h4 className="font-medium mb-2">Region Distribution</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">Advertisements by Region</h5>
              {Object.entries(testResults.regionDistribution.advertisements).map(([region, count]) => (
                <div key={region} className="flex justify-between text-sm">
                  <span>{getCountryNames([region])[0]} ({region})</span>
                  <span className="font-medium">{count as number}</span>
                </div>
              ))}
            </div>
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">Campaigns by Region</h5>
              {Object.entries(testResults.regionDistribution.campaigns).map(([region, count]) => (
                <div key={region} className="flex justify-between text-sm">
                  <span>{getCountryNames([region])[0]} ({region})</span>
                  <span className="font-medium">{count as number}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-4 text-sm text-gray-600">
        <p>Check the browser console for detailed test results.</p>
      </div>
    </div>
  );
};
